/**
 * Hook to capture console errors and warnings
 */

import { useEffect, useRef, useCallback } from 'react';
import type { ConsoleError } from '../types';

interface UseConsoleCaptureOptions {
  enabled: boolean;
  maxErrors: number;
}

interface UseConsoleCaptureResult {
  errors: ConsoleError[];
  clearErrors: () => void;
  addError: (error: ConsoleError) => void;
}

export function useConsoleCapture({
  enabled,
  maxErrors,
}: UseConsoleCaptureOptions): UseConsoleCaptureResult {
  const errorsRef = useRef<ConsoleError[]>([]);
  const originalConsoleError = useRef<typeof console.error | null>(null);
  const originalConsoleWarn = useRef<typeof console.warn | null>(null);

  const addError = useCallback(
    (error: ConsoleError) => {
      errorsRef.current = [error, ...errorsRef.current].slice(0, maxErrors);
    },
    [maxErrors]
  );

  const clearErrors = useCallback(() => {
    errorsRef.current = [];
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Store original methods
    originalConsoleError.current = console.error;
    originalConsoleWarn.current = console.warn;

    // Create error capturer
    const captureError = (type: 'error' | 'warn', args: unknown[]) => {
      const message = args
        .map((arg) => {
          if (arg instanceof Error) {
            return arg.message;
          }
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg);
            } catch {
              return String(arg);
            }
          }
          return String(arg);
        })
        .join(' ');

      const error: ConsoleError = {
        message,
        type,
        timestamp: new Date().toISOString(),
      };

      // Try to get stack trace
      if (args[0] instanceof Error) {
        error.stack = args[0].stack;
      } else {
        // Create a new error to get stack
        const stackError = new Error();
        if (stackError.stack) {
          // Remove the first few lines (this function and console override)
          const stackLines = stackError.stack.split('\n').slice(3);
          error.stack = stackLines.join('\n');

          // Try to parse source location
          const sourceMatch = stackLines[0]?.match(/at\s+.*?\s+\(?(.*?):(\d+):(\d+)\)?/);
          if (sourceMatch) {
            error.source = sourceMatch[1];
            error.line = parseInt(sourceMatch[2], 10);
            error.column = parseInt(sourceMatch[3], 10);
          }
        }
      }

      addError(error);
    };

    // Override console.error
    console.error = (...args: unknown[]) => {
      captureError('error', args);
      originalConsoleError.current?.apply(console, args);
    };

    // Override console.warn
    console.warn = (...args: unknown[]) => {
      captureError('warn', args);
      originalConsoleWarn.current?.apply(console, args);
    };

    // Also capture unhandled errors
    const handleError = (event: ErrorEvent) => {
      const error: ConsoleError = {
        message: event.message,
        type: 'error',
        timestamp: new Date().toISOString(),
        source: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack,
      };
      addError(error);
    };

    // Capture unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      const message =
        event.reason instanceof Error
          ? event.reason.message
          : String(event.reason);

      const error: ConsoleError = {
        message: `Unhandled Promise Rejection: ${message}`,
        type: 'error',
        timestamp: new Date().toISOString(),
        stack: event.reason instanceof Error ? event.reason.stack : undefined,
      };
      addError(error);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    // Cleanup
    return () => {
      if (originalConsoleError.current) {
        console.error = originalConsoleError.current;
      }
      if (originalConsoleWarn.current) {
        console.warn = originalConsoleWarn.current;
      }
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [enabled, addError]);

  return {
    errors: errorsRef.current,
    clearErrors,
    addError,
  };
}
